import React from 'react';
import { CornerDownLeft } from 'lucide-react';
import { HelpTooltip, HoverHelpTooltip } from '../ui/HelpTooltip';

interface ToggleControlsProps {
  webEnabled: boolean;
  setWebEnabled: (value: boolean) => void;
  enterSendsEnabled: boolean;
  setEnterSendsEnabled: (value: boolean) => void;
  selfEvalEnabled: boolean;
  setSelfEvalEnabled: (value: boolean) => void;
  txtEnabled: boolean;
  setTxtEnabled: (value: boolean) => void;
  targetCount?: number;
  // +Human feature
  plusHumanEnabled?: boolean;
  setPlusHumanEnabled?: (value: boolean) => void;
  humanLeaderboardEnabled?: boolean;
  setHumanLeaderboardEnabled?: (value: boolean) => void;
  // Admin feature
  showAdminInput?: boolean;
  setShowAdminInput?: (value: boolean) => void;
  adminConversationCount?: string;
  setAdminConversationCount?: (value: string) => void;
  onLoadAdminConversations?: (count: number) => void;
  isLoadingAdminConversations?: boolean;
  // Production mode - hides dev-only controls
  prodMode?: boolean;
  // Thinking/reasoning effort control
  reasoningEffort?: 'default' | 'low' | 'medium' | 'high';
  setReasoningEffort?: (value: 'default' | 'low' | 'medium' | 'high') => void;
  // Questionmark help system
  getHelpText?: (label: string) => string | null;
  hasHelp?: (label: string) => boolean;
  shouldShowHelp?: (label: string, hasHelpConfig: boolean) => boolean;
  markInteracted?: (label: string) => void;
}

export const ToggleControls: React.FC<ToggleControlsProps> = ({
  webEnabled,
  setWebEnabled,
  enterSendsEnabled,
  setEnterSendsEnabled,
  selfEvalEnabled,
  setSelfEvalEnabled,
  txtEnabled,
  setTxtEnabled,
  targetCount = 3,
  plusHumanEnabled = false,
  setPlusHumanEnabled,
  humanLeaderboardEnabled = true,
  setHumanLeaderboardEnabled,
  showAdminInput = false,
  setShowAdminInput,
  adminConversationCount = '',
  setAdminConversationCount,
  onLoadAdminConversations,
  isLoadingAdminConversations = false,
  prodMode = false,
  reasoningEffort = 'default',
  setReasoningEffort,
  getHelpText,
  hasHelp,
  shouldShowHelp,
  markInteracted,
}) => {
  const toggleButtonClass = (isActive: boolean) =>
    `inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
     transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
     ${isActive
       ? 'text-white border border-white/70 dark:border-white/70'
       : 'text-gray-400 border border-transparent'
     }`;

  return (
    <div className="flex items-center justify-center gap-2">
      {/* Web Grounding Toggle */}
      <HoverHelpTooltip
        helpText={getHelpText?.('web grounding') || ''}
        enabled={!shouldShowHelp?.('web grounding', hasHelp?.('web grounding') ?? false) && (hasHelp?.('web grounding') ?? false)}
      >
      <button
        onClick={() => {
          setWebEnabled(!webEnabled);
          if (markInteracted && hasHelp?.('web grounding')) markInteracted('web grounding');
        }}
        className={toggleButtonClass(webEnabled)}
        title="Enable/disable web search (:online modifier)"
      >
        web grounding
        {shouldShowHelp?.('web grounding', hasHelp?.('web grounding') ?? false) && (
          <HelpTooltip
            helpText={getHelpText?.('web grounding') || ''}
            onHelpViewed={() => markInteracted?.('web grounding')}
            size="sm"
          />
        )}
      </button>
      </HoverHelpTooltip>

      {/* Enter Sends Toggle */}
      <HoverHelpTooltip
        helpText={getHelpText?.('send') || ''}
        enabled={!shouldShowHelp?.('send', hasHelp?.('send') ?? false) && (hasHelp?.('send') ?? false)}
      >
      <button
        onClick={() => {
          setEnterSendsEnabled(!enterSendsEnabled);
          if (markInteracted && hasHelp?.('send')) markInteracted('send');
        }}
        className={toggleButtonClass(enterSendsEnabled)}
        title="Toggle whether Enter sends message or creates new line"
      >
        <CornerDownLeft className="h-3 w-3" />
        send
        {shouldShowHelp?.('send', hasHelp?.('send') ?? false) && (
          <HelpTooltip
            helpText={getHelpText?.('send') || ''}
            onHelpViewed={() => markInteracted?.('send')}
            size="sm"
          />
        )}
      </button>
      </HoverHelpTooltip>

      {/* Thinking Toggle - cycles through default → high → low → default */}
      {/* Always shown in high-contrast with outline since thinking is always enabled at some level */}
      {setReasoningEffort && (
        <HoverHelpTooltip
          helpText={getHelpText?.('thinking default') || ''}
          enabled={!shouldShowHelp?.('thinking default', hasHelp?.('thinking default') ?? false) && (hasHelp?.('thinking default') ?? false)}
        >
        <button
          onClick={() => {
            // Cycle: default → high → low → default (treat 'medium' as 'default')
            const nextEffort = (reasoningEffort === 'default' || reasoningEffort === 'medium') ? 'high' 
              : reasoningEffort === 'high' ? 'low' 
              : 'default';
            setReasoningEffort(nextEffort);
            if (markInteracted && hasHelp?.('thinking default')) markInteracted('thinking default');
          }}
          className="inline-flex items-center justify-center px-2 h-7 rounded text-xs font-medium
            transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
            text-white border border-white/70 dark:border-white/70"
          title={`Thinking: ${reasoningEffort} (click to cycle)`}
        >
          thinking {reasoningEffort === 'medium' ? 'default' : reasoningEffort}
          {shouldShowHelp?.('thinking default', hasHelp?.('thinking default') ?? false) && (
            <HelpTooltip
              helpText={getHelpText?.('thinking default') || ''}
              onHelpViewed={() => markInteracted?.('thinking default')}
              size="sm"
            />
          )}
        </button>
        </HoverHelpTooltip>
      )}

      {/* +Human Toggle - hidden in prodMode */}
      {!prodMode && setPlusHumanEnabled && (
        <button
          onClick={() => {
            const newValue = !plusHumanEnabled;
            setPlusHumanEnabled(newValue);
            // Force self-eval ON when +human is enabled
            if (newValue && !selfEvalEnabled) {
              setSelfEvalEnabled(true);
            }
          }}
          className={toggleButtonClass(plusHumanEnabled)}
          title="Enable/disable human response input"
        >
          <span className="text-sm">+</span>human
        </button>
      )}

      {/* Self-Eval Toggle - hidden when T=1, disabled when +human is ON, hidden in PROD_MODE */}
      {!prodMode && targetCount > 1 && (
        <button
          onClick={() => {
            if (!plusHumanEnabled) {
              const newValue = !selfEvalEnabled;
              console.log('🔘 Selfeval toggle clicked:', selfEvalEnabled, '→', newValue);
              setSelfEvalEnabled(newValue);
            }
          }}
          className={`${toggleButtonClass(selfEvalEnabled)} ${plusHumanEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={plusHumanEnabled 
            ? 'Self-eval required when +human is enabled' 
            : 'Enable/disable self-evaluation after ionoff response'
          }
          disabled={plusHumanEnabled}
        >
          selfeval
        </button>
      )}

      {/* TxT Toggle (Tournament grading) - hidden when T=1, hidden in PROD_MODE */}
      {!prodMode && targetCount > 1 && (
        <button
          onClick={() => setTxtEnabled(!txtEnabled)}
          className={toggleButtonClass(txtEnabled)}
          title="Enable/disable full round-robin tournament grading (every model grades every other model)"
        >
          TxT
        </button>
      )}

      {/* Admin: Load conversations from all users - hidden in PROD_MODE */}
      {!prodMode && setShowAdminInput && !showAdminInput && (
        <button
          onClick={() => setShowAdminInput(true)}
          className={toggleButtonClass(false)}
          title="Load recent conversations from all users"
        >
          admin
        </button>
      )}

      {/* Admin input (when expanded) - hidden in PROD_MODE */}
      {!prodMode && showAdminInput && setShowAdminInput && setAdminConversationCount && onLoadAdminConversations && (
        <>
          <input
            type="number"
            value={adminConversationCount}
            onChange={(e) => setAdminConversationCount(e.target.value)}
            placeholder="#"
            className="w-16 px-2 py-0.5 text-xs border border-border rounded bg-background text-foreground"
            min="1"
          />
          <button
            onClick={() => {
              console.log('🔵 Load button clicked, adminConversationCount:', adminConversationCount);
              const count = parseInt(adminConversationCount, 10);
              console.log('🔵 Parsed count:', count);
              if (count > 0) {
                console.log('🔵 Calling onLoadAdminConversations with count:', count);
                onLoadAdminConversations(count);
              } else {
                console.log('❌ Count is not > 0, not calling function');
              }
            }}
            disabled={isLoadingAdminConversations || !adminConversationCount}
            className={`${toggleButtonClass(false)} ${isLoadingAdminConversations || !adminConversationCount ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Load conversations"
          >
            {isLoadingAdminConversations ? '...' : 'load'}
          </button>
          <button
            onClick={() => {
              setShowAdminInput(false);
              setAdminConversationCount('');
            }}
            className={toggleButtonClass(false)}
            title="Cancel"
          >
            ✕
          </button>
        </>
      )}

      {/* Leaderboard Toggle - only visible when +human is ON, positioned at far right */}
      {plusHumanEnabled && setHumanLeaderboardEnabled && (
        <button
          onClick={() => setHumanLeaderboardEnabled(!humanLeaderboardEnabled)}
          className={toggleButtonClass(humanLeaderboardEnabled)}
          title={humanLeaderboardEnabled 
            ? 'Your grades will contribute to global +human leaderboard' 
            : 'Your grades will NOT contribute to global +human leaderboard'
          }
        >
          leaderboard
        </button>
      )}
    </div>
  );
};

